import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import pageMeta from './pageMeta.json';
import { fillPrices } from './fillPrices';

// Titles and descriptions of the public pages, with {{price:...}} placeholders filled from
// prices.json. scripts/prerender.js writes the same values into each page's static HTML at build
// time, so search engines see them without running JS.
export const PAGE_META = Object.fromEntries(Object.entries(pageMeta.pages).map(([path, page]) => [
  path,
  { title: fillPrices(page.title), description: fillPrices(page.description) },
]));

const { siteName: SITE_NAME, siteUrl: SITE_URL, defaultTitle: DEFAULT_TITLE, defaultDescription: DEFAULT_DESCRIPTION } = pageMeta;

function setMeta(selector, attr, value) {
  document.querySelector(selector)?.setAttribute(attr, value);
}

// Sets the browser tab title, meta description, canonical link and share (Open Graph)
// tags for a page. The defaults live in public/index.html.
export function usePageMeta(title, description) {
  const { pathname } = useLocation();
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
    const text = description || DEFAULT_DESCRIPTION;
    const url = `${SITE_URL}${pathname === '/' ? '/' : pathname.replace(/\/+$/, '')}`;
    document.title = fullTitle;
    setMeta('meta[name="description"]', 'content', text);
    setMeta('meta[property="og:title"]', 'content', fullTitle);
    setMeta('meta[property="og:description"]', 'content', text);
    setMeta('meta[property="og:url"]', 'content', url);
    setMeta('link[rel="canonical"]', 'href', url);
  }, [title, description, pathname]);
}

// Adds .is-visible to every .lx-reveal inside the returned ref once it scrolls into view.
export function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const items = root.querySelectorAll('.lx-reveal');
    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      }),
      // The huge top margin counts anything already scrolled past as visible, so a fast fling,
      // a jump or a restored scroll position never leaves a section invisible above the viewport.
      { rootMargin: '100000px 0px -10% 0px', threshold: 0.12 }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  });
  return ref;
}
